import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Search,
  FileText,
  Clock,
  Users,
  Calendar,
  ExternalLink,
  Tag,
  Filter,
  Eye,
  Scale,
  MapPin,
  Briefcase,
  GraduationCap,
  Heart,
  Shield,
  Zap,
  Leaf,
  Target,
  Globe,
  Building,
  DollarSign,
  Car,
  Gavel,
  Vote,
  Hammer,
  Users2,
  Award,
  TreePine,
  Settings,
  HardHat,
  UserCheck,
  Plane,
  Laptop,
  Coins,
  Truck,
  ScrollText,
  CheckCircle,
  XCircle,
  RotateCcw,
  Skull,
  PenTool,
  Landmark,
  FileCheck,
  AlertCircle,
  RefreshCw,
  ClipboardList,
  Flag,
  Pause,
  ChevronDown,
} from "lucide-react";
import InfoTooltip from "../components/InfoTooltip";
import DevelopmentBanner from "../components/DevelopmentBanner";
import Footer from "../components/Footer";
import AppNavbar from "../components/AppNavbar";

// Legislative term definitions for tooltips
const legislativeTermDefinitions = {
  Introduced:
    "A bill is formally presented to the legislature and assigned a number.",
  "House Review":
    "The House of Representatives examines and votes on the bill.",
  "Senate Review": "The Senate examines and votes on the bill.",
  "Chamber Review": "The originating chamber examines and votes on the bill.",
  "Second Chamber Review":
    "The second chamber (House or Senate) reviews the bill after it passes the first chamber.",
  "Governor's Desk": "The bill is sent to the Governor for approval or veto.",
  "Voter Approval":
    "Constitutional amendments require voter approval through a referendum.",
  "Final Decision":
    "The bill reaches its final outcome - becoming law, being vetoed, or failing.",
  "Signed into Law": "The Governor has approved the bill and it becomes law.",
  Adopted: "A resolution has been approved by the legislature.",
  Vetoed: "The Governor has rejected the bill.",
  Withdrawn: "The bill sponsor has removed the bill from consideration.",
  "Dead/Failed": "The bill did not pass and is no longer active.",
};

// Import separated components and utilities
import { timelineStyles } from "../styles/timelineStyles";
import LegislativeTimeline from "../components/LegislativeTimeline";
import BillCard from "../components/BillCard";
import { fetchSavedStatusesForBillIds, toggleSaveBill, resolveBillId } from "../lib/savedBills";
import { fetchFavoriteStatusesForBillIds, toggleFavoriteBill } from "../lib/favoritedBills";
import { buildApiUrl } from "../config";
import {
  formatDate,
  getDaysActive,
  getMostRecentActionDate,
  getCurrentBillStatus,
  getStatusColor,
  getBillStatus,
  truncateText,
} from "../scripts/bills/billUtils";
import {
  getPolicyTagIcon,
  getPolicyTagEmoji,
  getPolicyTagColor,
} from "../scripts/bills/policyTagUtils";

const Bills = ({ onBackToHome, isDarkMode, setIsDarkMode, featureControls }) => {
  const location = useLocation();
  const navigate = useNavigate();

  // Initialize state from cache if available (prevents reload on remount)
  const getInitialStateFromCache = () => {
    try {
      const cachedDataStr = sessionStorage.getItem("billsPageCache");
      if (cachedDataStr) {
        const cachedData = JSON.parse(cachedDataStr);
        return {
          bills: cachedData.bills || [],
          currentPage: cachedData.currentPage || 1,
          itemsPerPage: cachedData.itemsPerPage || 25,
          totalBills: cachedData.totalBills || 0,
          searchTerm: cachedData.searchTerm || "",
          selectedStatus: cachedData.selectedStatus || "all",
          selectedPolicyTag: cachedData.selectedPolicyTag || "all",
          selectedBillType: cachedData.selectedBillType || "all",
          selectedCommittee: cachedData.selectedCommittee || "all",
          selectedMemberId: cachedData.selectedMemberId || "all",
          selectedMemberSearch: cachedData.selectedMemberSearch || "",
          sortBy: cachedData.sortBy || "updated_desc",
          loading: false, // Don't show loading if we have cached data
        };
      }
    } catch (e) {
      console.error("Error reading cache on mount:", e);
    }
    return null;
  };

  const cachedState = getInitialStateFromCache();
  const [bills, setBills] = useState(cachedState?.bills || []);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(cachedState ? false : true);
  const [searchLoading, setSearchLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState(cachedState?.searchTerm || "");
  const [selectedStatus, setSelectedStatus] = useState(cachedState?.selectedStatus || "all");
  const [selectedPolicyTag, setSelectedPolicyTag] = useState(cachedState?.selectedPolicyTag || "all");
  const [selectedMemberId, setSelectedMemberId] = useState(cachedState?.selectedMemberId || "all");
  const [selectedMemberSearch, setSelectedMemberSearch] = useState(cachedState?.selectedMemberSearch || "");
  const [isMemberDropdownOpen, setIsMemberDropdownOpen] = useState(false);
  // Modal state removed - now using URL-based routing

  // Pagination state
  const [currentPage, setCurrentPage] = useState(cachedState?.currentPage || 1);
  const [itemsPerPage, setItemsPerPage] = useState(cachedState?.itemsPerPage || 25);
  const [totalBills, setTotalBills] = useState(cachedState?.totalBills || 0);
  const [directPageInput, setDirectPageInput] = useState("");

  // Additional filters
  const [selectedBillType, setSelectedBillType] = useState(cachedState?.selectedBillType || "all");
  const [selectedCommittee, setSelectedCommittee] = useState(cachedState?.selectedCommittee || "all");
  const [sortBy, setSortBy] = useState(cachedState?.sortBy || "updated_desc");

  // Policy tags state
  const [allPolicyTags, setAllPolicyTags] = useState([]);
  // Legislative members state (House/Senate) for sponsor/co-sponsor filtering
  const [allMembers, setAllMembers] = useState([]);
  // Committees state
  const [allCommittees, setAllCommittees] = useState([]);
  // Saved bills
  const [savedSet, setSavedSet] = useState(new Set());
  // Favorited bills
  const [favoritedSet, setFavoritedSet] = useState(new Set());

  const handleNavigateToBills = () => {
    navigate('/bills');
  };

  const handleNavigateToLocalBody = (bodySlug = 'warner-robins') => {
    navigate(`/local-body/${bodySlug}`);
  };

  // Request cancellation
  const abortControllerRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  // Flag to prevent URL parsing useEffect from running during programmatic navigation
  const isNavigating = useRef(false);
  // Flag to prevent other useEffects from running when URL parsing updates state
  const isUpdatingFromURL = useRef(false);
  // Flag to track when we're restoring from bill detail
  const isRestoringFromBillDetail = useRef(false);
  const memberFilterRef = useRef(null);

  // Memoize expensive calculations
  const memoizedGetCurrentBillStatus = useCallback(getCurrentBillStatus, []);
  // Helper to get latest action date excluding "Effective Date" (frontend-only fix)
  const getMostRecentActionDateExcludingEffective = useCallback((bill) => {
    if (!bill.history || bill.history.length === 0) {
      return getMostRecentActionDate(bill);
    }
    // Find first action that is NOT "Effective Date"
    const nonEffectiveAction = bill.history.find(action => {
      const actionLower = (action.action || '').toLowerCase();
      const code = action.code || '';
      return code !== 'EFF' && !actionLower.includes('effective date');
    });
    return nonEffectiveAction?.date || getMostRecentActionDate(bill);
  }, []);
  const memoizedGetMostRecentActionDate = useCallback(
    getMostRecentActionDateExcludingEffective,
    [getMostRecentActionDateExcludingEffective]
  );

  // CRITICAL: Prevent scroll to top IMMEDIATELY when returning from bill detail
  // This must run before any other effects to prevent React Router from scrolling
  useLayoutEffect(() => {
    const pathname = location.pathname;
    const isBillsPage = pathname === "/bills" || pathname.startsWith("/bills?");
    
    if (isBillsPage) {
      const hasSavedScrollPosition = 
        sessionStorage.getItem("billsPageScrollPosition") ||
        localStorage.getItem("billsPageScrollPosition");
      
      if (hasSavedScrollPosition) {
        // Immediately prevent any scroll to top behavior
        // This runs synchronously before React Router can scroll
        const targetPosition = parseInt(hasSavedScrollPosition);
        window.scrollTo(0, targetPosition);
        document.documentElement.scrollTop = targetPosition;
        document.body.scrollTop = targetPosition;
        console.log("🚫 [CRITICAL] Preventing scroll to top, restoring to:", targetPosition);
      }
    }
  }, [location.pathname]);

  // Restore bills data and scroll position when returning from bill detail
  // Use useLayoutEffect to run before other effects and prevent flicker
  useLayoutEffect(() => {
    const pathname = location.pathname;
    const isBillsPage = pathname === "/bills" || pathname.startsWith("/bills?");

    // Only check for restoration when on bills page
    if (!isBillsPage) {
      return;
    }

    // Check for cached bills data (indicates we're returning from bill detail)
    const cachedDataStr = sessionStorage.getItem("billsPageCache");
    const savedScrollPosition =
      sessionStorage.getItem("billsPageScrollPosition") ||
      localStorage.getItem("billsPageScrollPosition");

    if (cachedDataStr && savedScrollPosition) {
      try {
        const cachedData = JSON.parse(cachedDataStr);
        console.log("🔄 [PRODUCTION DEBUG] Restoring cached bills data (useLayoutEffect):", cachedData);

        // Set flags FIRST to prevent any other effects from running
        isRestoringFromBillDetail.current = true;
        isNavigating.current = false;

        // Restore bills state from cache
        setBills(cachedData.bills || []);
        setCurrentPage(cachedData.currentPage || 1);
        setItemsPerPage(cachedData.itemsPerPage || 25);
        setTotalBills(cachedData.totalBills || 0);
        setSearchTerm(cachedData.searchTerm || "");
        setSelectedStatus(cachedData.selectedStatus || "all");
        setSelectedPolicyTag(cachedData.selectedPolicyTag || "all");
        setSelectedBillType(cachedData.selectedBillType || "all");
        setSelectedCommittee(cachedData.selectedCommittee || "all");
        setSelectedMemberId(cachedData.selectedMemberId || "all");
        setSelectedMemberSearch(cachedData.selectedMemberSearch || "");
        setSortBy(cachedData.sortBy || "updated_desc");
        setLoading(false);
        setSearchLoading(false);
        setError(null);

        // Restore scroll position with multiple attempts to prevent React Router from overriding
        const targetPosition = parseInt(savedScrollPosition);
        console.log("🔄 [SCROLL] Restoring scroll position to:", targetPosition);

        // Immediate restoration (synchronously in useLayoutEffect)
        window.scrollTo(0, targetPosition);
        document.documentElement.scrollTop = targetPosition;
        document.body.scrollTop = targetPosition;

        // Additional restoration attempts to catch any late scroll-to-top events
        requestAnimationFrame(() => {
          window.scrollTo(0, targetPosition);
          document.documentElement.scrollTop = targetPosition;
          document.body.scrollTop = targetPosition;
        });

        // Another attempt after a short delay
        setTimeout(() => {
          window.scrollTo(0, targetPosition);
          document.documentElement.scrollTop = targetPosition;
          document.body.scrollTop = targetPosition;
          console.log("🔄 [SCROLL] Final scroll restoration attempt at:", targetPosition);
        }, 100);

        // DON'T clear cache yet - let URL parsing useEffect see it and skip
        // We'll clear it after a delay to ensure all effects have checked it

        // Reset flag and clear cache after ensuring all effects have run
        setTimeout(() => {
          isRestoringFromBillDetail.current = false;
          sessionStorage.removeItem("billsPageCache");
          console.log("🔄 [PRODUCTION DEBUG] Reset restoration flag and cleared cache");
        }, 2000);
      } catch (e) {
        console.error("Error restoring cached bills data:", e);
        sessionStorage.removeItem("billsPageCache");
      }
    } else if (cachedDataStr) {
      // If we have cache but no scroll position, something went wrong - clear it
      console.warn("🔄 Found cache but no scroll position - clearing cache");
      sessionStorage.removeItem("billsPageCache");
    }
  }, [location.pathname]); // Trigger when pathname changes (returning from bill detail)

  // Restore scroll position immediately when returning to bills page (before React Router scrolls)
  // This is a fallback if cached data restoration didn't handle scroll
  useLayoutEffect(() => {
    // Check if we're on the bills page (not bill detail)
    const pathname = location.pathname;
    const isBillsPage = pathname === "/bills" || pathname.startsWith("/bills?");
    const isBillDetailUrl = /^\/bills\/[^/]+$/.test(pathname);

    // If navigating to bill detail, preserve scroll position (don't scroll to top)
    if (isBillDetailUrl) {
      // Don't scroll - preserve current position
      return;
    }

    if (!isBillsPage) return;

    // Check both sessionStorage and localStorage for saved scroll position
    const savedScrollPosition =
      sessionStorage.getItem("billsPageScrollPosition") ||
      localStorage.getItem("billsPageScrollPosition");

    if (savedScrollPosition) {
      const targetPosition = parseInt(savedScrollPosition);
      console.log(
        "🔄 [SCROLL] Restoring scroll position (fallback useLayoutEffect):",
        targetPosition
      );

      // Restore scroll position immediately to prevent React Router from scrolling to top
      window.scrollTo(0, targetPosition);
      document.documentElement.scrollTop = targetPosition;
      document.body.scrollTop = targetPosition;

      // Additional attempts to ensure scroll is restored
      requestAnimationFrame(() => {
        window.scrollTo(0, targetPosition);
        document.documentElement.scrollTop = targetPosition;
        document.body.scrollTop = targetPosition;
      });

      setTimeout(() => {
        window.scrollTo(0, targetPosition);
        document.documentElement.scrollTop = targetPosition;
        document.body.scrollTop = targetPosition;
      }, 50);
    }
  }, [location.pathname]); // Trigger when pathname changes

  // Restore scroll position after bills are loaded (when returning from bill detail)
  // This is a final safety net to ensure scroll is restored even if earlier attempts failed
  useEffect(() => {
    // Only restore if we're on bills page and have saved scroll position
    const pathname = location.pathname;
    const isBillsPage = pathname === "/bills" || pathname.startsWith("/bills?");
    if (!isBillsPage) return;

    // Check both sessionStorage and localStorage for saved scroll position
    const savedScrollPosition =
      sessionStorage.getItem("billsPageScrollPosition") ||
      localStorage.getItem("billsPageScrollPosition");

    if (savedScrollPosition && bills.length > 0 && !loading) {
      const targetPosition = parseInt(savedScrollPosition);
      const currentPosition = window.scrollY;

      // Only restore if we're not already at the target position (within 10px tolerance)
      if (Math.abs(currentPosition - targetPosition) > 10) {
        console.log(
          "🔄 [SCROLL] Final restoration attempt - current:",
          currentPosition,
          "target:",
          targetPosition
        );

        // Use a more aggressive scroll restoration approach
        const scrollToPosition = (position) => {
          // Method 1: window.scrollTo (most reliable)
          window.scrollTo({
            top: position,
            left: 0,
            behavior: "auto", // Use 'auto' for instant scroll, not 'smooth'
          });

          // Method 2: Direct assignment as backup
          requestAnimationFrame(() => {
            if (Math.abs(window.scrollY - position) > 10) {
              document.documentElement.scrollTop = position;
              document.body.scrollTop = position;
            }
          });

          // Method 3: Another attempt after a short delay
          setTimeout(() => {
            if (Math.abs(window.scrollY - position) > 10) {
              document.documentElement.scrollTop = position;
              document.body.scrollTop = position;
              window.scrollTo(0, position);
            }
          }, 100);
        };

        scrollToPosition(targetPosition);
      }
    }
  }, [bills, loading, location.pathname]); // Trigger when bills are loaded and not loading

  // Load saved and favorited statuses when bills change
  useEffect(() => {
    (async () => {
      try {
        const billIds = bills.map((b) => b.id).filter(Boolean);
        if (billIds.length === 0) {
          setSavedSet(new Set());
          setFavoritedSet(new Set());
          return;
        }
        const [saved, favorited] = await Promise.all([
          fetchSavedStatusesForBillIds(billIds),
          fetchFavoriteStatusesForBillIds(billIds)
        ]);
        setSavedSet(saved);
        setFavoritedSet(favorited);
      } catch (e) {
        // ignore
      }
    })();
  }, [bills]);

  const handleToggleSave = useCallback(async (bill) => {
    try {
      const billId = bill.id || (await resolveBillId(bill));
      if (!billId) {
        alert("Unable to save this bill (missing id).");
        return;
      }
      const isSaved = savedSet.has(billId);
      const newState = await toggleSaveBill(billId, !isSaved);
      setSavedSet((prev) => {
        const next = new Set(prev);
        if (newState) next.add(billId);
        else next.delete(billId);
        return next;
      });
    } catch (e) {
      alert(e?.message || "Failed to update saved state.");
    }
  }, [savedSet]);

  const handleToggleFavorite = useCallback(async (bill) => {
    try {
      const billId = bill.id || (await resolveBillId(bill));
      if (!billId) {
        alert("Unable to favorite this bill (missing id).");
        return;
      }
      const isFav = favoritedSet.has(billId);
      const newState = await toggleFavoriteBill(billId, !isFav);
      setFavoritedSet((prev) => {
        const next = new Set(prev);
        if (newState) next.add(billId);
        else next.delete(billId);
        return next;
      });
    } catch (e) {
      alert(e?.message || "Failed to update favorite state.");
    }
  }, [favoritedSet]);

  // Helper function for timeline icons
  const getTimelineIcon = useCallback((emojiString) => {
    // Map emoji strings to Lucide React icon components
    const iconMap = {
      "🧾": ScrollText,
      "📄": FileText,
      "🏛️": Landmark,
      "✍️": PenTool,
      "⚖️": Scale,
      "✅": CheckCircle,
      "❌": XCircle,
      "↩️": RotateCcw,
      "💀": Skull,
      "📜": ScrollText,
      "🗳️": Vote,
      "💤": Pause,
    };

    return iconMap[emojiString] || FileText;
  }, []);

  // Parse URL parameters and fetch bills when URL changes or component mounts
  useEffect(() => {
    // Check if we're on a bill detail URL (e.g., /bills/HB131) and skip parsing
    const pathname = location.pathname;
    // Allow spaces and other characters in bill numbers (e.g. "HB 123")
    const isBillDetailUrl = /^\/bills\/[^/]+$/.test(pathname);
    const isBillsPage = pathname === "/bills" || pathname.startsWith("/bills?");

    // Clear scroll position only when navigating away from bills entirely
    if (!isBillDetailUrl && !isBillsPage) {
      console.log(
        "🔄 [PRODUCTION DEBUG] Navigating away from bills, clearing saved scroll position"
      );
      sessionStorage.removeItem("billsPageScrollPosition");
      localStorage.removeItem("billsPageScrollPosition");
      sessionStorage.removeItem("billsPageCache");
    }

    // CRITICAL: If we're returning from bill detail, skip ALL URL parsing to prevent reload
    const hasSavedScrollPos = 
      sessionStorage.getItem("billsPageScrollPosition") ||
      localStorage.getItem("billsPageScrollPosition");
    const hasCachedBillsData = sessionStorage.getItem("billsPageCache");
    
    if (isBillsPage && (hasSavedScrollPos || hasCachedBillsData)) {
      console.log("🚫 [CRITICAL] Returning from bill detail - skipping URL parsing to prevent reload");
      // Set flag to prevent any fetching
      isRestoringFromBillDetail.current = true;
      // Don't parse URL or update state - keep everything as is
      // The restoration useLayoutEffect will handle state restoration
      return;
    }

    // If on bill detail URL, don't fetch bills - just preserve scroll position and bills state
    if (isBillDetailUrl) {
      console.log("🔄 Skipping URL parsing and fetch - on bill detail URL:", pathname);
      // Don't clear loading state or reset anything - keep bills as they are
      // Don't reset navigation flags either - preserve state
      return;
    }

    // CRITICAL: Check for cached data FIRST - if we have it, we're restoring from bill detail
    // Skip ALL fetching logic to prevent reload
    const hasCachedData = sessionStorage.getItem("billsPageCache");
    if (hasCachedData && isBillsPage) {
      console.log("🔄 [CRITICAL] Has cached bills data - COMPLETELY skipping fetch to prevent reload");
      // Set flag to prevent any other effects from interfering
      isRestoringFromBillDetail.current = true;
      isNavigating.current = false;
      // Don't fetch - restoration useLayoutEffect will handle state restoration
      // Just ensure loading is false if we have bills
      if (bills.length > 0) {
        setLoading(false);
      }
      return;
    }

    // EARLY CHECK: If we're returning from bill detail (has saved scroll position)
    // and bills are already loaded, skip fetching to prevent reload
    const hasSavedScrollPosition = sessionStorage.getItem("billsPageScrollPosition") ||
      localStorage.getItem("billsPageScrollPosition");
    if (hasSavedScrollPosition && isBillsPage && bills.length > 0 && !loading) {
      console.log("🔄 Returning from bill detail - skipping fetch, keeping existing bills, will restore scroll");
      // Set flag to prevent other effects from interfering
      isRestoringFromBillDetail.current = true;
      // Reset navigation flag
      isNavigating.current = false;
      // Don't fetch bills again - they're already loaded
      // Scroll restoration will happen in useLayoutEffect
      return;
    }

    // Skip if we're in the middle of programmatic navigation
    if (isNavigating.current) {
      console.log(
        "🔄 Skipping URL parsing - programmatic navigation in progress"
      );
      return;
    }

    // Skip if we're restoring from bill detail and already have bills loaded
    // This prevents unnecessary reload when returning from bill detail
    if (isRestoringFromBillDetail.current && bills.length > 0 && !loading) {
      console.log(
        "🔄 Skipping URL parsing and fetch - restoring from bill detail, bills already loaded"
      );
      return;
    }

    // Only reset navigation flags when actually on bills page (not bill detail)
    // And only if we're not restoring from bill detail
    if (isBillsPage && !isRestoringFromBillDetail.current) {
      console.log("🔄 Resetting navigation state for bills page");
      isNavigating.current = false;
      isRestoringFromBillDetail.current = false;

      // Reset abort controller to prevent stuck requests
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();

      // Only clear loading states if we're not restoring
      setLoading(false);
      setSearchLoading(false);
      setError(null);
    }

    // FINAL CHECK: If we're restoring from bill detail, DO NOT FETCH
    if (isRestoringFromBillDetail.current) {
      console.log("🔄 [FINAL CHECK] Skipping fetch - restoring from bill detail");
      return;
    }

    console.log(
      "🔄 URL parsing useEffect triggered, isNavigating:",
      isNavigating.current,
      "isRestoring:",
      isRestoringFromBillDetail.current
    );

    // Set flag to prevent other useEffects from running during URL parsing
    isUpdatingFromURL.current = true;

    const searchParams = new URLSearchParams(location.search);
    const page = parseInt(searchParams.get("page")) || 1;
    const search = searchParams.get("search") || "";
    const status = searchParams.get("status") || "all";
    const policyTag = searchParams.get("policyTag") || "all";
    const billType = searchParams.get("billType") || "all";
    const committee = searchParams.get("committee") || "all";
    const member = searchParams.get("member") || "all";
    const sortByParam = searchParams.get("sortBy") || "updated_desc";
    const limit = parseInt(searchParams.get("limit")) || 25;

    console.log("🔄 URL change detected, updating state and fetching bills:", {
      page,
      search,
      status,
      policyTag,
      billType,
      committee,
      member,
      sortByParam,
      limit,
    });

    // Update state with URL parameters
    setCurrentPage(page);
    setSearchTerm(search);
    setSelectedStatus(status);
    setSelectedPolicyTag(policyTag);
    setSelectedBillType(billType);
    setSelectedCommittee(committee);
    setSelectedMemberId(member);
    setSortBy(sortByParam);
    setItemsPerPage(limit);

    // Fetch bills with the URL parameters directly
    // Always fetch bills when on bills page, even with default parameters
    // BUT NOT if we're restoring from bill detail
    if (!isRestoringFromBillDetail.current) {
      console.log("🔄 Calling fetchBills with page:", page);
      fetchBills(
        page,
        limit,
        search,
        null,
        status,
        policyTag,
        billType,
        committee,
        sortByParam,
        member
      );
    } else {
      console.log("🔄 Skipping fetchBills - restoring from bill detail");
    }

    // Reset flag after a short delay to allow state updates to complete
    setTimeout(() => {
      console.log("🔄 Resetting URL update flag");
      isUpdatingFromURL.current = false;
    }, 100);
  }, [location.search]); // Only depend on location.search - pathname changes are handled separately

  useEffect(() => {
    // Only fetch sessions, policy tags, and committees on mount, bills are handled by URL-based useEffect
    fetchSessions();
    fetchAllPolicyTags();
    fetchCommittees();
    fetchAllLegislativeMembers();

  }, []); // Only run on mount

  // Cleanup effect to cancel pending requests and clear loading states
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      // Clear loading states on unmount to prevent stuck states
      setLoading(false);
      setSearchLoading(false);
      setError(null);
      // Clear saved scroll position on unmount (navigating away from bills)
      sessionStorage.removeItem("billsPageScrollPosition");
      localStorage.removeItem("billsPageScrollPosition");
      console.log(
        "🔄 [PRODUCTION DEBUG] Component unmounting, cleared saved scroll position"
      );
    };
  }, []);

  // Listen for bill search events from voting records
  useEffect(() => {
    const handleSearchBill = (event) => {
      const billNumber = event.detail;
      if (billNumber) {
        console.log(`🔍 Searching for bill ${billNumber} from voting records`);
        setSearchTerm(billNumber);

        // Automatically trigger the search (same logic as handleSearchSubmit)
        const newPage = 1;
        setCurrentPage(newPage);
        isNavigating.current = true;
        const params = new URLSearchParams();
        params.append("page", newPage.toString());
        if (itemsPerPage !== 25)
          params.append("limit", itemsPerPage.toString());
        params.append("search", billNumber);
        if (selectedStatus !== "all") params.append("status", selectedStatus);
        if (selectedPolicyTag !== "all")
          params.append("policyTag", selectedPolicyTag);
        if (selectedBillType !== "all")
          params.append("billType", selectedBillType);
        if (selectedCommittee !== "all")
          params.append("committee", selectedCommittee);
        if (selectedMemberId !== "all")
          params.append("member", selectedMemberId);
        if (sortBy !== "updated_desc") params.append("sortBy", sortBy);
        const newURL = `/bills?${params.toString()}`;
        navigate(newURL, { replace: true });
        fetchBills(
          newPage,
          itemsPerPage,
          billNumber,
          null,
          selectedStatus,
          selectedPolicyTag,
          selectedBillType,
          selectedCommittee,
          sortBy,
          selectedMemberId
        );
        setTimeout(() => {
          isNavigating.current = false;
        }, 500);

        // Scroll to top and ensure bill is visible
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    };

    window.addEventListener("searchBill", handleSearchBill);

    return () => {
      window.removeEventListener("searchBill", handleSearchBill);
    };
  }, [
    itemsPerPage,
    selectedStatus,
    selectedPolicyTag,
    selectedBillType,
    selectedCommittee,
    selectedMemberId,
    sortBy,
    navigate,
  ]);

  // Handle search submit explicitly (Enter or button), no per-keystroke searching
  const handleSearchSubmit = useCallback(
    (e) => {
      if (e) e.preventDefault();
      // Skip if we're updating from URL to prevent conflicts
      if (isUpdatingFromURL.current) {
        return;
      }
      const newPage = 1;
      setCurrentPage(newPage);
      isNavigating.current = true;
      const params = new URLSearchParams();
      params.append("page", newPage.toString());
      if (itemsPerPage !== 25) params.append("limit", itemsPerPage.toString());
      if (searchTerm) params.append("search", searchTerm);
      if (selectedStatus !== "all") params.append("status", selectedStatus);
      if (selectedPolicyTag !== "all")
        params.append("policyTag", selectedPolicyTag);
      if (selectedBillType !== "all")
        params.append("billType", selectedBillType);
      if (selectedCommittee !== "all")
        params.append("committee", selectedCommittee);
      if (selectedMemberId !== "all")
        params.append("member", selectedMemberId);
      if (sortBy !== "updated_desc") params.append("sortBy", sortBy);
      const newURL = `/bills?${params.toString()}`;
      navigate(newURL, { replace: true });
      fetchBills(
        newPage,
        itemsPerPage,
        searchTerm,
        null,
        selectedStatus,
        selectedPolicyTag,
        selectedBillType,
        selectedCommittee,
        sortBy,
        selectedMemberId
      );
      setTimeout(() => {
        isNavigating.current = false;
      }, 500);
    },
    [
      itemsPerPage,
      searchTerm,
      selectedStatus,
      selectedPolicyTag,
      selectedBillType,
      selectedCommittee,
      selectedMemberId,
      sortBy,
      navigate,
    ]
  );

  // Handle other filter changes (non-search)
  useEffect(() => {
    // Check if we're on a bill detail URL and skip filter changes
    // Check if we're on a bill detail URL (e.g., /bills/HB 123) and skip parsing
    const pathname = location.pathname;
    // Allow spaces and other characters in bill numbers
    const isBillDetailUrl = /^\/bills\/[^/]+$/.test(pathname);

    if (isBillDetailUrl) {
      console.log("🔄 Skipping filter changes - on bill detail URL:", pathname);
      return;
    }

    console.log("🔄 Filter change detected:", {
      selectedStatus,
      selectedPolicyTag,
      selectedBillType,
      selectedCommittee,
      selectedMemberId,
      sortBy,
    });

    // Skip if we're updating from URL to prevent conflicts
    if (isUpdatingFromURL.current) {
      console.log(
        "🔄 Skipping filter changes useEffect - URL update in progress"
      );
      return;
    }

    // Skip if we're navigating to prevent conflicts
    if (isNavigating.current) {
      console.log(
        "🔄 Skipping filter changes useEffect - navigation in progress"
      );
      return;
    }

    // Skip if we're restoring from bill detail to prevent scroll position interference
    // But still allow bills to be fetched when returning to /bills
    if (isRestoringFromBillDetail.current && pathname !== "/bills") {
      console.log(
        "🔄 Skipping filter changes useEffect - restoring from bill detail"
      );
      return;
    }

    console.log("🔄 Processing filter change - calling fetchBills");
    const newPage = 1; // Reset to page 1 when filters change
    setCurrentPage(newPage);
    // Set flag to prevent URL parsing useEffect from running
    isNavigating.current = true;
    // Update URL immediately with the new state
    const params = new URLSearchParams();
    params.append("page", newPage.toString());
    if (itemsPerPage !== 25) params.append("limit", itemsPerPage.toString());
    if (searchTerm) params.append("search", searchTerm);
    if (selectedStatus !== "all") params.append("status", selectedStatus);
    if (selectedPolicyTag !== "all")
      params.append("policyTag", selectedPolicyTag);
    if (selectedBillType !== "all") params.append("billType", selectedBillType);
    if (selectedCommittee !== "all") params.append("committee", selectedCommittee);
    if (selectedMemberId !== "all") params.append("member", selectedMemberId);
    if (sortBy !== "updated_desc") params.append("sortBy", sortBy);

    const newURL = `/bills?${params.toString()}`;
    console.log("🔄 Updating URL to:", newURL);
    navigate(newURL, { replace: true });
    fetchBills(
      newPage,
      itemsPerPage,
      searchTerm,
      null,
      selectedStatus,
      selectedPolicyTag,
      selectedBillType,
      selectedCommittee,
      sortBy,
      selectedMemberId
    );
    // Reset flag after navigation
    setTimeout(() => {
      console.log("🔄 Resetting navigation flag for filter changes");
      isNavigating.current = false;
    }, 500);
  }, [
    selectedStatus,
    selectedPolicyTag,
    selectedBillType,
    selectedCommittee,
    selectedMemberId,
    sortBy,
    itemsPerPage,
    navigate,
  ]);

  const fetchBills = async (
    page = 1,
    limit = itemsPerPage,
    search = null,
    session = null,
    status = null,
    policyTag = null,
    billType = null,
    committee = null,
    sortBy = null,
    memberId = null,
    retryCount = 0
  ) => {
    // CRITICAL: Skip if we're restoring from bill detail or have cached data
    if (isRestoringFromBillDetail.current) {
      console.log("🔄 fetchBills: Skipping - restoring from bill detail");
      return;
    }

    // Also check for cache - if it exists, we're in the middle of restoration
    const hasCachedData = sessionStorage.getItem("billsPageCache");
    if (hasCachedData) {
      console.log("🔄 fetchBills: Skipping - cached data exists (restoration in progress)");
      return;
    }

    // Safety mechanism: always ensure loading states are cleared
    const clearLoadingStates = () => {
      setLoading(false);
      setSearchLoading(false);
    };

    try {
      console.log(
        "🔄 fetchBills called with page:",
        page,
        "limit:",
        limit,
        "retry:",
        retryCount
      );

      // Prevent infinite recursion
      if (retryCount > 2) {
        console.error("❌ Too many retries, falling back to page 1");
        setCurrentPage(1);
        setError("Unable to load the requested page. Redirected to page 1.");
        clearLoadingStates();
        fetchBills(
          1,
          limit,
          search,
          session,
          status,
          policyTag,
          billType,
          committee,
          sortBy,
          memberId
        );
        return;
      }

      // Cancel previous request if it exists
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Create new abort controller
      abortControllerRef.current = new AbortController();

      // Use provided parameters or fall back to current state
      const finalSearch = search !== null ? search : searchTerm;
      const finalSession = session !== null ? session : "all";
      const finalStatus = status !== null ? status : selectedStatus;
      const finalPolicyTag = policyTag !== null ? policyTag : selectedPolicyTag;
      const finalBillType = billType !== null ? billType : selectedBillType;
      const finalCommittee = committee !== null ? committee : selectedCommittee;
      const finalMemberId = memberId !== null ? memberId : selectedMemberId;
      const finalDateRange = "all";
      const finalSortBy = sortBy !== null ? sortBy : "updated_desc";

      // Set appropriate loading state
      // Skip loading state if we already have bills loaded (prevents flash when returning from bill detail)
      if (finalSearch && page === 1) {
        setSearchLoading(true);
      } else if (bills.length === 0) {
        // Only show loading on initial load or when no bills are present
        setLoading(true);
      } else {
        // Keep existing bills visible while fetching (prevents flash)
        setSearchLoading(true);
      }

      // Use regular offset-based pagination
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        search: finalSearch || "",
        session: finalSession || "all",
        status: finalStatus || "all",
        policyTag: finalPolicyTag || "all",
        billType: finalBillType || "all",
        committee: finalCommittee || "all",
        member: finalMemberId || "all",
        dateRange: finalDateRange || "all",
        sortBy: finalSortBy || "updated_desc",
      });

      const apiUrl = buildApiUrl(`/api/bills?${params.toString()}`);

      console.log("🔄 Making API request to:", apiUrl);

      // Add timeout to prevent hanging requests
      const timeoutId = setTimeout(() => {
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
        }
      }, 30000); // 30 second timeout

      const response = await fetch(apiUrl, {
        signal: abortControllerRef.current.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        // Log the full error response for debugging
        console.error(`❌ HTTP error! status: ${response.status}`);

        let errorData = null;
        try {
          const errorText = await response.text();
          console.error("❌ Error response body:", errorText);
          // Try to parse as JSON if possible
          try {
            errorData = JSON.parse(errorText);
          } catch (e) {
            // Not JSON, that's fine
          }
        } catch (e) {
          console.error("❌ Could not read error response body");
        }

        // Handle specific error cases
        if (response.status === 400 && errorData?.error === "Page number too high") {
          // If page is too high, redirect to suggested page
          const suggestedPage =
            errorData.maxValidPage || errorData.suggestedPage || 1;
          console.warn(
            `Page ${page} is too high, redirecting to page ${suggestedPage}`
          );
          setCurrentPage(suggestedPage);
          fetchBills(suggestedPage, limit);
          return;
        }

        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log("📊 Loaded bills from API:", data);

      setBills(data.bills || data); // Handle both paginated and non-paginated responses
      setTotalBills(data.total || data.length || 0);
      setCurrentPage(page);

      // URL is now updated by the pagination functions to avoid race conditions
    } catch (err) {
      // Don't set error if request was cancelled
      if (err.name === "AbortError") {
        console.log("Request cancelled");
        clearLoadingStates();
        return;
      }
      console.error("❌ Error fetching bills:", err);

      // Provide more specific error messages for pagination issues
      if (page > 50) {
        setError(
          `Failed to load page ${page}. This might be due to server limitations. Try a lower page number.`
        );
      } else {
        setError(
          "Failed to load bills. Please check if the server is running."
        );
      }

      // Ensure loading states are cleared on error
      clearLoadingStates();
    } finally {
      // Additional safety: always clear loading states
      clearLoadingStates();
    }
  };

  const fetchSessions = async () => {
    try {
      const response = await fetch(buildApiUrl("/api/sessions"));
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log("📋 Loaded sessions from API:", data);
      setSessions(data);
    } catch (err) {
      console.error("❌ Error fetching sessions:", err);
    }
  };

  const fetchAllPolicyTags = async () => {
    try {
      const response = await fetch(buildApiUrl("/api/policy-tags"));
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log("🏷️ Loaded all policy tags from API:", data);
      // Extract the name property from each policy tag object
      const tagNames = data
        .map((tag) => (typeof tag === "string" ? tag : tag.name))
        .filter(Boolean);
      setAllPolicyTags(tagNames.sort());
    } catch (err) {
      console.error("❌ Error fetching policy tags:", err);
      // Fallback: extract from current bills if API fails
      const fallbackTags = [
        ...new Set(
          bills.flatMap((bill) => {
            if (!bill.policy_tags) return [];
            return Array.isArray(bill.policy_tags)
              ? bill.policy_tags.filter(Boolean)
              : [bill.policy_tags].filter(Boolean);
          })
        ),
      ].filter(Boolean);
      setAllPolicyTags(fallbackTags.sort());
    }
  };

  const fetchCommittees = async () => {
    try {
      const response = await fetch(buildApiUrl("/api/committees"));
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log("🏛️ Loaded committees from API:", data);
      setAllCommittees(data);
    } catch (err) {
      console.error("❌ Error fetching committees:", err);
    }
  };

  const fetchAllLegislativeMembers = async () => {
    try {
      const response = await fetch(buildApiUrl("/api/bills/members"));
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      const members = Array.isArray(data?.members) ? data.members : [];
      setAllMembers(members);
    } catch (err) {
      console.error("Error fetching legislative members:", err);
      setAllMembers([]);
    }
  };

  const handleBillClick = useCallback(
    (bill) => {
      // Save current scroll position of the bills page before opening bill detail
      const currentScrollPosition = window.scrollY;
      console.log("💾 Saving scroll position before opening bill:", currentScrollPosition);
      sessionStorage.setItem(
        "billsPageScrollPosition",
        currentScrollPosition.toString()
      );
      // Also save to localStorage as backup
      localStorage.setItem(
        "billsPageScrollPosition",
        currentScrollPosition.toString()
      );

      // Cache current bills data to prevent reload when returning
      const billsCache = {
        bills: bills,
        currentPage: currentPage,
        itemsPerPage: itemsPerPage,
        totalBills: totalBills,
        searchTerm: searchTerm,
        selectedStatus: selectedStatus,
        selectedPolicyTag: selectedPolicyTag,
        selectedBillType: selectedBillType,
        selectedCommittee: selectedCommittee,
        selectedMemberId: selectedMemberId,
        selectedMemberSearch: selectedMemberSearch,
        sortBy: sortBy,
        url: `${location.pathname}${location.search}`
      };
      sessionStorage.setItem("billsPageCache", JSON.stringify(billsCache));
      console.log("💾 Cached bills data to prevent reload:", billsCache);

      // Preserve current page state for return navigation
      const returnUrl = `${location.pathname}${location.search}`;

      // Set flag to prevent bills from reloading
      isNavigating.current = true;

      // Prevent scroll to top before navigation
      window.history.scrollRestoration = 'manual';

      // Navigate to bill detail - prevent scroll to top
      // Remove spaces from bill number in URL for cleaner URLs (e.g., "SB 120" -> "SB120")
      const cleanBillNumber = bill.bill_number.replace(/\s+/g, '');
      navigate(`/bills/${cleanBillNumber}`, {
        state: { returnUrl, preserveScroll: true },
        replace: false, // Use push, not replace, so back button works
      });
    },
    [navigate, location, bills, currentPage, itemsPerPage, totalBills, searchTerm, selectedStatus, selectedPolicyTag, selectedBillType, selectedCommittee, selectedMemberId, selectedMemberSearch, sortBy]
  );

  // handleCloseModal removed - now using URL-based routing

  // Create policy tag options with icons
  const policyTagOptions = useMemo(() => {
    const baseOptions = [{ value: "all", label: "All Policy Tags", icon: Tag }];

    const tagOptions = allPolicyTags.map((tag) => ({
      value: tag,
      label: tag,
      icon: getPolicyTagIcon(tag),
    }));

    return [...baseOptions, ...tagOptions];
  }, [allPolicyTags]);

  // Create committee options grouped by chamber
  const committeeOptions = useMemo(() => {
    const baseOptions = [{ value: "all", label: "All Committees", icon: Building }];

    // Group committees by chamber and add chamber prefix for clarity
    const committeeOptionsList = allCommittees.map((committee) => ({
      value: committee.id,
      label: `${committee.chamber} - ${committee.name}`,
      icon: Building,
    }));

    return [...baseOptions, ...committeeOptionsList];
  }, [allCommittees]);

  const memberOptions = useMemo(() => {
    const normalizeChamber = (value) => {
      const chamberValue = (value || "").toLowerCase();
      if (chamberValue === "house") return "House";
      if (chamberValue === "senate") return "Senate";
      return value || "Legislature";
    };

    const options = (allMembers || [])
      .filter((member) => member?.id && member?.name)
      .map((member) => {
        const chamber = normalizeChamber(member.chamber);
        const district =
          member.district_number !== null && member.district_number !== undefined
            ? `District ${member.district_number}`
            : "No District";
        return {
          value: member.id,
          label: `${member.name} (${chamber} ${district})`,
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label));

    return [{ value: "all", label: "All Members" }, ...options];
  }, [allMembers]);

  const filteredMemberOptions = useMemo(() => {
    const query = selectedMemberSearch.trim().toLowerCase();
    if (!query) return [];
    return memberOptions
      .filter((option) => option.value !== "all")
      .filter((option) => option.label.toLowerCase().includes(query))
      .slice(0, 20);
  }, [memberOptions, selectedMemberSearch]);

  // Helper function to get bill type colors
  const getBillTypeColor = (billType, isDarkMode) => {
    const billTypeColors = {
      SB: isDarkMode
        ? "bg-blue-900/50 text-blue-300"
        : "bg-blue-100 text-blue-800",
      HB: isDarkMode
        ? "bg-green-900/50 text-green-300"
        : "bg-green-100 text-green-800",
      SR: isDarkMode
        ? "bg-purple-900/50 text-purple-300"
        : "bg-purple-100 text-purple-800",
      HR: isDarkMode
        ? "bg-orange-900/50 text-orange-300"
        : "bg-orange-100 text-orange-800",
    };
    return (
      billTypeColors[billType] ||
      (isDarkMode ? "bg-gray-700 text-gray-300" : "bg-gray-100 text-gray-800")
    );
  };

  // Helper function to get sort by colors
  const getSortByColor = (sortBy, isDarkMode) => {
    const sortByColors = {
      updated_asc: isDarkMode
        ? "bg-indigo-900/50 text-indigo-300"
        : "bg-indigo-100 text-indigo-800",
    };
    return (
      sortByColors[sortBy] ||
      (isDarkMode ? "bg-gray-700 text-gray-300" : "bg-gray-100 text-gray-800")
    );
  };

  // Dropdown options
  const itemsPerPageOptions = [
    { value: 10, label: "10 bills" },
    { value: 25, label: "25 bills" },
    { value: 50, label: "50 bills" },
    { value: 100, label: "100 bills" },
    { value: 200, label: "200 bills" },
  ];

  const sortByOptions = [
    { value: "updated_desc", label: "Newest First" },
    { value: "updated_asc", label: "Oldest First" },
  ];

  const sessionOptions = useMemo(() => {
    const options = [{ value: "all", label: "All Sessions" }];

    sessions.forEach((session) => {
      options.push({
        value: (session.session_id || session.id || '').toString(),
        label: session.session_name || session.name || 'Unknown Session',
      });
    });

    return options;
  }, [sessions]);

  const statusOptions = [
    { value: "all", label: "All Statuses" },
    { value: "Introduced", label: "Introduced" },
    { value: "In Committee", label: "In Committee" },
    { value: "On the Floor", label: "On the Floor" },
    { value: "Passed One Chamber", label: "Passed House/Senate" },
    { value: "Passed Both Chambers", label: "Passed Both Chambers" },
    { value: "Signed into Law", label: "Signed into Law" },
    { value: "Resolving Differences", label: "Resolving Differences" },
    { value: "Vetoed", label: "Vetoed" },
    { value: "Dead", label: "Dead" },
    { value: "Adopted", label: "Adopted" },
    // Inactive/Stalled removed as per user request
    // { value: "Inactive/Stalled", label: "Inactive/Stalled" },
  ];

  const billTypeOptions = [
    { value: "all", label: "All Types" },
    { value: "SB", label: "Senate Bills" },
    { value: "HB", label: "House Bills" },
    { value: "SR", label: "Senate Resolutions" },
    { value: "HR", label: "House Resolutions" },
  ];

  useEffect(() => {
    if (selectedMemberId === "all") return;
    const selectedMember = memberOptions.find(
      (option) => option.value === selectedMemberId
    );
    if (selectedMember && selectedMember.label !== selectedMemberSearch) {
      setSelectedMemberSearch(selectedMember.label);
    }
  }, [selectedMemberId, selectedMemberSearch, memberOptions]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (!memberFilterRef.current) return;
      if (!memberFilterRef.current.contains(event.target)) {
        setIsMemberDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, []);

  // Memoize pagination calculations
  const paginationInfo = useMemo(() => {
    const totalPages = Math.ceil(totalBills / itemsPerPage);
    const startItem = (currentPage - 1) * itemsPerPage + 1;
    const endItem = Math.min(currentPage * itemsPerPage, totalBills);
    return { totalPages, startItem, endItem };
  }, [totalBills, itemsPerPage, currentPage]);

  // Helper functions for pagination
  const goToPage = useCallback(
    (page) => {
      if (page >= 1 && page <= paginationInfo.totalPages) {
        setCurrentPage(page);
        // Set flag to prevent URL parsing useEffect from running
        isNavigating.current = true;
        // Update URL immediately with the new page
        const params = new URLSearchParams();
        params.append("page", page.toString());
        if (itemsPerPage !== 25)
          params.append("limit", itemsPerPage.toString());
        if (searchTerm) params.append("search", searchTerm);
        if (selectedStatus !== "all") params.append("status", selectedStatus);
        if (selectedPolicyTag !== "all")
          params.append("policyTag", selectedPolicyTag);
        if (selectedBillType !== "all")
          params.append("billType", selectedBillType);
        if (selectedCommittee !== "all")
          params.append("committee", selectedCommittee);
        if (selectedMemberId !== "all")
          params.append("member", selectedMemberId);
        if (sortBy !== "updated_desc") params.append("sortBy", sortBy);

        const newURL = `/bills?${params.toString()}`;
        navigate(newURL, { replace: true });
        fetchBills(
          page,
          itemsPerPage,
          searchTerm,
          null,
          selectedStatus,
          selectedPolicyTag,
          selectedBillType,
          selectedCommittee,
          sortBy,
          selectedMemberId
        );
        // Reset flag after navigation
        setTimeout(() => {
          console.log("🔄 Resetting navigation flag for goToPage");
          isNavigating.current = false;
        }, 500);
      }
    },
    [
      paginationInfo.totalPages,
      itemsPerPage,
      searchTerm,
      selectedStatus,
      selectedPolicyTag,
      selectedBillType,
      selectedCommittee,
      selectedMemberId,
      sortBy,
      navigate,
    ]
  );

  const handleDirectPageNavigation = useCallback(() => {
    const page = parseInt(directPageInput);
    if (page && page >= 1 && page <= paginationInfo.totalPages) {
      setCurrentPage(page);
      // Set flag to prevent URL parsing useEffect from running
      isNavigating.current = true;
      // Update URL immediately with the new page
      const params = new URLSearchParams();
      params.append("page", page.toString());
      if (itemsPerPage !== 25) params.append("limit", itemsPerPage.toString());
      if (searchTerm) params.append("search", searchTerm);
      if (selectedStatus !== "all") params.append("status", selectedStatus);
      if (selectedPolicyTag !== "all")
        params.append("policyTag", selectedPolicyTag);
      if (selectedBillType !== "all")
        params.append("billType", selectedBillType);
      if (selectedCommittee !== "all")
        params.append("committee", selectedCommittee);
      if (selectedMemberId !== "all")
        params.append("member", selectedMemberId);
      if (sortBy !== "updated_desc") params.append("sortBy", sortBy);

      const newURL = `/bills?${params.toString()}`;
      navigate(newURL, { replace: true });
      fetchBills(
        page,
        itemsPerPage,
        searchTerm,
        null,
        selectedStatus,
        selectedPolicyTag,
        selectedBillType,
        selectedCommittee,
        sortBy,
        selectedMemberId
      );
      setDirectPageInput(page.toString());
      // Reset flag after navigation
      setTimeout(() => {
        console.log(
          "🔄 Resetting navigation flag for handleDirectPageNavigation"
        );
        isNavigating.current = false;
      }, 500);
    }
  }, [
    directPageInput,
    paginationInfo.totalPages,
    itemsPerPage,
    searchTerm,
    selectedStatus,
    selectedPolicyTag,
    selectedBillType,
    selectedCommittee,
    selectedMemberId,
    sortBy,
    navigate,
  ]);

  // Update directPageInput when currentPage changes
  useEffect(() => {
    setDirectPageInput(currentPage.toString());
  }, [currentPage]);

  const handleItemsPerPageChange = useCallback(
    (newItemsPerPage) => {
      // Calculate what page the current first bill should be on with the new limit
      const currentFirstBillIndex = (currentPage - 1) * itemsPerPage;
      const newPage = Math.floor(currentFirstBillIndex / newItemsPerPage) + 1;

      console.log(
        `🔄 Changing items per page from ${itemsPerPage} to ${newItemsPerPage}`
      );
      console.log(
        `🔄 Current page: ${currentPage}, current first bill index: ${currentFirstBillIndex}`
      );
      console.log(`🔄 New page should be: ${newPage}`);

      setItemsPerPage(newItemsPerPage);
      setCurrentPage(newPage);

      // Set flag to prevent URL parsing useEffect from running
      isNavigating.current = true;

      // Update URL immediately with the calculated page
      const params = new URLSearchParams();
      params.append("page", newPage.toString());
      if (newItemsPerPage !== 25)
        params.append("limit", newItemsPerPage.toString());
      if (searchTerm) params.append("search", searchTerm);
      if (selectedStatus !== "all") params.append("status", selectedStatus);
      if (selectedPolicyTag !== "all")
        params.append("policyTag", selectedPolicyTag);
      if (selectedBillType !== "all")
        params.append("billType", selectedBillType);
      if (selectedCommittee !== "all")
        params.append("committee", selectedCommittee);
      if (selectedMemberId !== "all")
        params.append("member", selectedMemberId);
      if (sortBy !== "updated_desc") params.append("sortBy", sortBy);

      const newURL = `/bills?${params.toString()}`;
      navigate(newURL, { replace: true });

      // Fetch bills with the new page and limit to maintain position
      fetchBills(newPage, newItemsPerPage);

      // Reset flag after navigation
      setTimeout(() => {
        console.log(
          "🔄 Resetting navigation flag for handleItemsPerPageChange"
        );
        isNavigating.current = false;
      }, 500);
    },
    [
      currentPage,
      itemsPerPage,
      searchTerm,
      selectedStatus,
      selectedPolicyTag,
      selectedBillType,
      selectedCommittee,
      selectedMemberId,
      sortBy,
      navigate,
      fetchBills,
    ]
  );

  // Memoize bill status calculations for the list
  const billsWithStatus = useMemo(() => {
    return bills.map((bill) => {
      // BillCard now handles status computation internally using the new system
      return {
        ...bill,
        mostRecentDate: memoizedGetMostRecentActionDate(bill),
      };
    });
  }, [bills, memoizedGetMostRecentActionDate]);

  if (loading) {
    return (
      <div
        className={`min-h-screen ${isDarkMode ? "bg-gray-900" : "bg-gray-50"
          } flex items-center justify-center`}
      >
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className={isDarkMode ? "text-gray-300" : "text-gray-600"}>
            Loading Georgia bills...
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className={`min-h-screen ${isDarkMode ? "bg-gray-900" : "bg-gray-50"
          } flex items-center justify-center`}
      >
        <div className="text-center">
          <FileText
            className={`w-16 h-16 ${isDarkMode ? "text-gray-700" : "text-gray-300"
              } mx-auto mb-4`}
          />
          <h3
            className={`text-lg font-medium ${isDarkMode ? "text-gray-300" : "text-gray-900"
              } mb-2`}
          >
            Failed to load bills
          </h3>
          <p
            className={`${isDarkMode ? "text-gray-400" : "text-gray-600"} mb-4`}
          >
            {error}
          </p>
          <button
            onClick={fetchBills}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <style>{timelineStyles}</style>
      <div
        className={`min-h-screen w-full overflow-x-hidden ${isDarkMode ? "bg-gray-900 dark" : "bg-gray-50"
          }`}
      >
        <AppNavbar
          isDarkMode={isDarkMode}
          setIsDarkMode={setIsDarkMode}
          showBackButton={true}
          onBackToHome={onBackToHome}
          featureControls={featureControls}
        />

        <DevelopmentBanner isDarkMode={isDarkMode} />

        {/* Search and Filters */}
        <section className="py-4 md:py-8 w-full">
          <div className="w-full max-w-7xl mx-auto px-3 sm:px-4 lg:px-8">
            {/* Legislative Timeline - Prominent placement */}
            <div className="mb-8">
              <LegislativeTimeline isDarkMode={isDarkMode} />
            </div>

            <div
              className={`${isDarkMode ? "bg-gray-800" : "bg-white"
                } rounded-2xl shadow-xl p-4 md:p-8 border ${isDarkMode ? "border-gray-700" : "border-gray-100"
                } mb-6 md:mb-8 w-full`}
            >
              <div className="text-center mb-8">
                <div
                  className={`inline-flex items-center justify-center w-12 h-12 rounded-full ${isDarkMode
                    ? "bg-[#3b82f6]"
                    : "bg-[#3b82f6]"
                    } mb-6 mr-2`}
                >
                  <FileText
                    className={`w-6 h-6 text-white`}
                  />
                </div>
                <h2
                  className={`text-3xl font-bold ${isDarkMode ? "text-white" : "text-gray-900"
                    } mb-3 inline-flex items-center justify-center gap-2`}
                >
                  Georgia Legislative Bills
                </h2>
                <p
                  className={`text-lg ${isDarkMode ? "text-gray-300" : "text-gray-600"
                    } mb-2`}
                >
                  Track legislative activity and bill progress
                </p>
                <p
                  className={`text-base ${isDarkMode ? "text-gray-400" : "text-gray-500"
                    } mb-8`}
                >
                  Showing {paginationInfo.startItem}-{paginationInfo.endItem} of{" "}
                  {totalBills.toLocaleString()} bills
                  {paginationInfo.totalPages > 1 &&
                    ` • Page ${currentPage} of ${paginationInfo.totalPages}`}
                </p>
              </div>

              {/* Search */}
              <form className="relative mb-6" onSubmit={handleSearchSubmit}>
                <Search
                  className={`absolute left-4 top-1/2 transform -translate-y-1/2 ${isDarkMode ? "text-gray-400" : "text-gray-400"
                    } w-5 h-5`}
                />
                {searchLoading && (
                  <div className="absolute right-4 top-1/2 transform -translate-y-1/2">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                  </div>
                )}
                <input
                  type="text"
                  placeholder="Search by bill number, title, or description..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  disabled={searchLoading}
                  className={`w-full pl-12 pr-6 py-3 text-base border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all shadow-sm ${isDarkMode
                    ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                    : "bg-white border-gray-200 text-gray-900 placeholder-gray-500"
                    } ${searchLoading ? "opacity-75 cursor-not-allowed" : ""}`}
                />
              </form>

              <div className="mb-6" ref={memberFilterRef}>
                <label
                  htmlFor="member-filter"
                  className={`flex items-center text-sm font-semibold ${isDarkMode ? "text-gray-300" : "text-gray-700"} mb-3`}
                >
                  <Users className="w-4 h-4 mr-2" />
                  Sponsor / Co-Sponsor Member
                </label>
                <div className="relative">
                  <Users className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`} />
                  <input
                    id="member-filter"
                    type="text"
                    value={selectedMemberSearch}
                    placeholder="Type to find a House or Senate member..."
                    autoComplete="off"
                    onFocus={() => setIsMemberDropdownOpen(true)}
                    onChange={(e) => {
                      const value = e.target.value;
                      setSelectedMemberSearch(value);
                      setIsMemberDropdownOpen(true);
                      if (!value.trim()) {
                        setSelectedMemberId("all");
                        return;
                      }
                      const matchedMember = memberOptions.find((option) => option.label === value);
                      if (matchedMember) {
                        setSelectedMemberId(matchedMember.value);
                      } else {
                        setSelectedMemberId("all");
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Escape") {
                        setIsMemberDropdownOpen(false);
                      }
                      if (e.key === "Enter" && filteredMemberOptions.length > 0) {
                        e.preventDefault();
                        const first = filteredMemberOptions[0];
                        setSelectedMemberId(first.value);
                        setSelectedMemberSearch(first.label);
                        setIsMemberDropdownOpen(false);
                      }
                    }}
                    className={`w-full pl-10 pr-4 py-3 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all ${isDarkMode
                      ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                      : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                      }`}
                  />
                  {isMemberDropdownOpen && selectedMemberSearch.trim().length > 0 && (
                    <div
                      className={`absolute z-20 mt-1 w-full max-h-60 overflow-y-auto rounded-lg border shadow-lg ${isDarkMode
                        ? "bg-gray-800 border-gray-700"
                        : "bg-white border-gray-200"
                        }`}
                    >
                      {filteredMemberOptions.length > 0 ? (
                        filteredMemberOptions.map((option) => (
                          <button
                            key={option.value}
                            type="button"
                            onClick={() => {
                              setSelectedMemberId(option.value);
                              setSelectedMemberSearch(option.label);
                              setIsMemberDropdownOpen(false);
                            }}
                            className={`w-full px-3 py-2 text-left text-sm transition-colors ${isDarkMode
                              ? "text-gray-100 hover:bg-gray-700"
                              : "text-gray-900 hover:bg-gray-100"
                              }`}
                          >
                            {option.label}
                          </button>
                        ))
                      ) : (
                        <div className={`px-3 py-2 text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
                          No matching members
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <p className={`mt-2 text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
                  Select a member to show bills they have sponsored or co-sponsored.
                </p>
              </div>

              {/* Filters */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4 mb-6">
                {/* Items per page selector */}
                <div>
                  <label
                    htmlFor="items-per-page"
                    className={`flex items-center text-sm font-semibold ${isDarkMode ? "text-gray-300" : "text-gray-700"
                      } mb-3`}
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    Show per page
                  </label>
                  <div className="relative">
                    <Eye className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`} />
                    <select
                      id="items-per-page"
                      value={itemsPerPage}
                      onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                      className={`w-full pl-10 pr-10 py-3 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all ${isDarkMode
                        ? "bg-gray-700 border-gray-600 text-white"
                        : "bg-white border-gray-300 text-gray-900"
                        } appearance-none cursor-pointer`}
                    >
                      {itemsPerPageOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className={`absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 pointer-events-none ${isDarkMode ? "text-gray-400" : "text-gray-500"}`} />
                  </div>
                </div>

                {/* Sort by selector */}
                <div>
                  <label
                    htmlFor="sort-by"
                    className={`flex items-center text-sm font-semibold ${isDarkMode ? "text-gray-300" : "text-gray-700"
                      } mb-3`}
                  >
                    <Filter className="w-4 h-4 mr-2" />
                    Sort by
                  </label>
                  <div className="relative">
                    <Filter className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`} />
                    <select
                      id="sort-by"
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      className={`w-full pl-10 pr-10 py-3 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all ${isDarkMode
                        ? "bg-gray-700 border-gray-600 text-white"
                        : "bg-white border-gray-300 text-gray-900"
                        } appearance-none cursor-pointer`}
                    >
                      {sortByOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className={`absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 pointer-events-none ${isDarkMode ? "text-gray-400" : "text-gray-500"}`} />
                  </div>
                </div>

                {/* Status Dropdown */}
                <div>
                  <label
                    htmlFor="bill-status"
                    className={`flex items-center text-sm font-semibold ${isDarkMode ? "text-gray-300" : "text-gray-700"
                      } mb-3`}
                  >
                    <Scale className="w-4 h-4 mr-2" />
                    <InfoTooltip
                      content="Bill status shows where a bill is in the legislative process: introduced, in review, passed by one chamber, or signed into law. Bills can also be vetoed or fail to pass."
                      title="Understanding Bill Status"
                      size="sm"
                      isDarkMode={isDarkMode}
                    >
                      Bill Status
                    </InfoTooltip>
                  </label>
                  <div className="relative">
                    <Scale className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`} />
                    <select
                      id="bill-status"
                      value={selectedStatus}
                      onChange={(e) => setSelectedStatus(e.target.value)}
                      className={`w-full pl-10 pr-10 py-3 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all ${isDarkMode
                        ? "bg-gray-700 border-gray-600 text-white"
                        : "bg-white border-gray-300 text-gray-900"
                        } appearance-none cursor-pointer`}
                    >
                      {statusOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className={`absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 pointer-events-none ${isDarkMode ? "text-gray-400" : "text-gray-500"}`} />
                  </div>
                </div>

                {/* Bill Type Dropdown */}
                <div>
                  <label
                    htmlFor="bill-type"
                    className={`flex items-center text-sm font-semibold ${isDarkMode ? "text-gray-300" : "text-gray-700"
                      } mb-3`}
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    <InfoTooltip
                      content="HB = House Bill (introduced in House), SB = Senate Bill (introduced in Senate), HR = House Resolution, SR = Senate Resolution. Bills become laws, resolutions express opinions or create committees."
                      title="Bill Type Codes"
                      size="sm"
                      isDarkMode={isDarkMode}
                    >
                      Bill Type
                    </InfoTooltip>
                  </label>
                  <div className="relative">
                    <FileText className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`} />
                    <select
                      id="bill-type"
                      value={selectedBillType}
                      onChange={(e) => setSelectedBillType(e.target.value)}
                      className={`w-full pl-10 pr-10 py-3 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all ${isDarkMode
                        ? "bg-gray-700 border-gray-600 text-white"
                        : "bg-white border-gray-300 text-gray-900"
                        } appearance-none cursor-pointer`}
                    >
                      {billTypeOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className={`absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 pointer-events-none ${isDarkMode ? "text-gray-400" : "text-gray-500"}`} />
                  </div>
                </div>

                {/* Policy Tags Dropdown */}
                <div>
                  <label
                    htmlFor="policy-tags"
                    className={`flex items-center text-sm font-semibold ${isDarkMode ? "text-gray-300" : "text-gray-700"
                      } mb-3`}
                  >
                    <Tag className="w-4 h-4 mr-2" />
                    <InfoTooltip
                      content="Policy tags categorize bills by subject area (education, healthcare, transportation, etc.). This helps you find bills related to issues you care about."
                      title="Policy Categories"
                      size="sm"
                      isDarkMode={isDarkMode}
                    >
                      Policy Tags
                    </InfoTooltip>
                  </label>
                  <div className="relative">
                    <Tag className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`} />
                    <select
                      id="policy-tags"
                      value={selectedPolicyTag}
                      onChange={(e) => setSelectedPolicyTag(e.target.value)}
                      className={`w-full pl-10 pr-10 py-3 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all ${isDarkMode
                        ? "bg-gray-700 border-gray-600 text-white"
                        : "bg-white border-gray-300 text-gray-900"
                        } appearance-none cursor-pointer`}
                    >
                      {policyTagOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className={`absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 pointer-events-none ${isDarkMode ? "text-gray-400" : "text-gray-500"}`} />
                  </div>
                </div>

                {/* Committee Dropdown */}
                <div>
                  <label
                    htmlFor="committee"
                    className={`flex items-center text-sm font-semibold ${isDarkMode ? "text-gray-300" : "text-gray-700"
                      } mb-3`}
                  >
                    <Building className="w-4 h-4 mr-2" />
                    <InfoTooltip
                      content="Filter bills by the legislative committee handling them. Committees review bills in their subject area before they go to the full chamber for a vote."
                      title="Committee Filter"
                      size="sm"
                      isDarkMode={isDarkMode}
                    >
                      Committee
                    </InfoTooltip>
                  </label>
                  <div className="relative">
                    <Building className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`} />
                    <select
                      id="committee"
                      value={selectedCommittee}
                      onChange={(e) => setSelectedCommittee(e.target.value)}
                      className={`w-full pl-10 pr-10 py-3 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all ${isDarkMode
                        ? "bg-gray-700 border-gray-600 text-white"
                        : "bg-white border-gray-300 text-gray-900"
                        } appearance-none cursor-pointer`}
                    >
                      {committeeOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className={`absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 pointer-events-none ${isDarkMode ? "text-gray-400" : "text-gray-500"}`} />
                  </div>
                </div>
              </div>

              {/* Active Filters Display */}
              {(selectedStatus !== "all" ||
                selectedPolicyTag !== "all" ||
                selectedBillType !== "all" ||
                selectedCommittee !== "all" ||
                selectedMemberId !== "all" ||
                sortBy !== "updated_desc") && (
                  <div className="mt-6 flex flex-wrap items-center gap-2">
                    <span
                      className={`text-sm font-medium ${isDarkMode ? "text-gray-400" : "text-gray-600"
                        }`}
                    >
                      Active filters:
                    </span>
                    {selectedStatus !== "all" && (
                      <span
                        className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(
                          selectedStatus,
                          isDarkMode
                        )}`}
                      >
                        <Scale className="w-3 h-3 mr-1" />
                        {getBillStatus(selectedStatus)}
                        <button
                          onClick={() => setSelectedStatus("all")}
                          className="ml-1 hover:opacity-75"
                        >
                          ×
                        </button>
                      </span>
                    )}
                    {selectedPolicyTag !== "all" && (
                      <span
                        className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${getPolicyTagColor(
                          selectedPolicyTag,
                          isDarkMode
                        )}`}
                      >
                        {React.createElement(
                          getPolicyTagIcon(selectedPolicyTag),
                          { className: "w-3 h-3 mr-1" }
                        )}
                        {selectedPolicyTag}
                        <button
                          onClick={() => setSelectedPolicyTag("all")}
                          className="ml-1 hover:opacity-75"
                        >
                          ×
                        </button>
                      </span>
                    )}
                    {selectedBillType !== "all" && (
                      <span
                        className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${getBillTypeColor(
                          selectedBillType,
                          isDarkMode
                        )}`}
                      >
                        <FileText className="w-3 h-3 mr-1" />
                        {selectedBillType}
                        <button
                          onClick={() => setSelectedBillType("all")}
                          className="ml-1 hover:opacity-75"
                        >
                          ×
                        </button>
                      </span>
                    )}
                    {selectedCommittee !== "all" && (
                      <span
                        className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${isDarkMode ? "bg-teal-900/50 text-teal-300" : "bg-teal-100 text-teal-800"
                          }`}
                      >
                        <Building className="w-3 h-3 mr-1" />
                        {committeeOptions.find((c) => c.value === selectedCommittee)?.label || "Committee"}
                        <button
                          onClick={() => setSelectedCommittee("all")}
                          className="ml-1 hover:opacity-75"
                        >
                          ×
                        </button>
                      </span>
                    )}
                    {selectedMemberId !== "all" && (
                      <span
                        className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${isDarkMode ? "bg-cyan-900/50 text-cyan-300" : "bg-cyan-100 text-cyan-800"}`}
                      >
                        <Users className="w-3 h-3 mr-1" />
                        {memberOptions.find((option) => option.value === selectedMemberId)?.label || "Selected Member"}
                        <button
                          onClick={() => {
                            setSelectedMemberId("all");
                            setSelectedMemberSearch("");
                          }}
                          className="ml-1 hover:opacity-75"
                        >
                          Ã—
                        </button>
                      </span>
                    )}
                    {sortBy !== "updated_desc" && (
                      <span
                        className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${getSortByColor(
                          sortBy,
                          isDarkMode
                        )}`}
                      >
                        <Filter className="w-3 h-3 mr-1" />
                        {sortByOptions.find((option) => option.value === sortBy)
                          ?.label || sortBy}
                        <button
                          onClick={() => setSortBy("updated_desc")}
                          className="ml-1 hover:opacity-75"
                        >
                          ×
                        </button>
                      </span>
                    )}
                    <button
                      onClick={() => {
                        setSelectedStatus("all");
                        setSelectedPolicyTag("all");
                        setSelectedBillType("all");
                        setSelectedCommittee("all");
                        setSelectedMemberId("all");
                        setSelectedMemberSearch("");
                        setSortBy("updated_desc");
                      }}
                      className={`text-xs font-medium ${isDarkMode
                        ? "text-gray-400 hover:text-gray-300"
                        : "text-gray-500 hover:text-gray-700"
                        } underline`}
                    >
                      Clear all filters
                    </button>
                  </div>
                )}
            </div>
          </div>
        </section>

        {/* Bills List */}
        <section className="pb-8 md:pb-12 w-full">
          <div className="w-full max-w-7xl mx-auto px-3 sm:px-4 lg:px-8">
            <div className="space-y-4">
              {billsWithStatus.map((bill) => (
                <BillCard
                  key={bill._id}
                  bill={bill}
                  isDarkMode={isDarkMode}
                  onBillClick={handleBillClick}
                  formatDate={formatDate}
                  getPolicyTagIcon={getPolicyTagIcon}
                  getPolicyTagColor={(tag) =>
                    getPolicyTagColor(tag, isDarkMode)
                  }
                  getDaysActive={getDaysActive}
                  isSaved={bill.id ? savedSet.has(bill.id) : false}
                  onToggleSave={handleToggleSave}
                  isFavorited={bill.id ? favoritedSet.has(bill.id) : false}
                  onToggleFavorite={handleToggleFavorite}
                />
              ))}
            </div>

            {bills.length === 0 && !loading && (
              <div className="text-center py-12">
                <FileText
                  className={`w-16 h-16 ${isDarkMode ? "text-gray-600" : "text-gray-300"
                    } mx-auto mb-4`}
                />
                <h3
                  className={`text-lg font-medium ${isDarkMode ? "text-gray-300" : "text-gray-900"
                    } mb-2`}
                >
                  No bills found
                </h3>
                <p className={isDarkMode ? "text-gray-400" : "text-gray-600"}>
                  {searchTerm
                    ? "Try adjusting your search criteria or filters."
                    : "No bills match your current filters."}
                </p>
              </div>
            )}

            {/* Pagination Controls */}
            {paginationInfo.totalPages > 1 && totalBills > 0 && (
              <div
                className={`mt-8 flex flex-col md:flex-row items-center justify-between gap-4 ${isDarkMode ? "bg-gray-800" : "bg-white"
                  } rounded-xl p-4 shadow-sm border ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}
              >
                {/* Result count - Left side */}
                <span
                  className={`text-sm font-medium ${isDarkMode ? "text-gray-400" : "text-gray-500"
                    }`}
                >
                  Showing {paginationInfo.startItem}-{paginationInfo.endItem} of {totalBills.toLocaleString()} bills
                </span>

                {/* Pagination buttons - Center/Right */}
                <div className="flex items-center gap-2">
                  {/* Previous button */}
                  <button
                    onClick={() => goToPage(currentPage - 1)}
                    disabled={currentPage === 1}
                    className={`p-2 rounded-lg transition-colors ${currentPage === 1
                      ? isDarkMode
                        ? "text-gray-600 cursor-not-allowed"
                        : "text-gray-300 cursor-not-allowed"
                      : isDarkMode
                        ? "text-gray-300 hover:bg-gray-700 hover:text-white"
                        : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
                      }`}
                    aria-label="Previous Page"
                  >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>

                  {/* Page numbers */}
                  <div className="flex items-center gap-1">
                    {/* First page */}
                    {currentPage > 3 && (
                      <>
                        <button
                          onClick={() => goToPage(1)}
                          className={`w-8 h-8 flex items-center justify-center rounded-lg text-sm font-medium transition-colors ${isDarkMode
                            ? "text-gray-300 hover:bg-gray-700"
                            : "text-gray-600 hover:bg-gray-100"
                            }`}
                        >
                          1
                        </button>
                        <span className={`text-sm ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>...</span>
                      </>
                    )}

                    {/* Page range */}
                    {(() => {
                      let startPage = Math.max(1, currentPage - 1);
                      let endPage = Math.min(paginationInfo.totalPages, currentPage + 1);
                      if (endPage - startPage < 2) {
                        if (startPage === 1) {
                          endPage = Math.min(paginationInfo.totalPages, startPage + 2);
                        } else if (endPage === paginationInfo.totalPages) {
                          startPage = Math.max(1, endPage - 2);
                        }
                      }
                      const pages = [];
                      for (let i = startPage; i <= endPage; i++) pages.push(i);
                      return pages.map((pageNum) => (
                        <button
                          key={pageNum}
                          onClick={() => goToPage(pageNum)}
                          className={`w-8 h-8 flex items-center justify-center rounded-lg text-sm font-medium transition-all ${pageNum === currentPage
                            ? "bg-blue-600 text-white shadow-sm"
                            : isDarkMode
                              ? "text-gray-300 hover:bg-gray-700"
                              : "text-gray-600 hover:bg-gray-100"
                            }`}
                        >
                          {pageNum}
                        </button>
                      ));
                    })()}

                    {/* Last page */}
                    {currentPage < paginationInfo.totalPages - 2 && (
                      <>
                        <span className={`text-sm ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>...</span>
                        <button
                          onClick={() => goToPage(paginationInfo.totalPages)}
                          className={`w-8 h-8 flex items-center justify-center rounded-lg text-sm font-medium transition-colors ${isDarkMode
                            ? "text-gray-300 hover:bg-gray-700"
                            : "text-gray-600 hover:bg-gray-100"
                            }`}
                        >
                          {paginationInfo.totalPages}
                        </button>
                      </>
                    )}
                  </div>

                  {/* Next button */}
                  <button
                    onClick={() => goToPage(currentPage + 1)}
                    disabled={currentPage === paginationInfo.totalPages}
                    className={`p-2 rounded-lg transition-colors ${currentPage === paginationInfo.totalPages
                      ? isDarkMode
                        ? "text-gray-600 cursor-not-allowed"
                        : "text-gray-300 cursor-not-allowed"
                      : isDarkMode
                        ? "text-gray-300 hover:bg-gray-700 hover:text-white"
                        : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
                      }`}
                    aria-label="Next Page"
                  >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>
              </div>
            )}
          </div>
        </section>
      </div>
      <Footer isDarkMode={isDarkMode} />
    </>
  );
};

export default Bills;
